import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppModalService } from '@datachain/ui-sdk/components/app-modal';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
	catchError,
	combineLatest,
	filter,
	map,
	of,
	switchMap,
	tap,
} from 'rxjs';

import { NgCommService } from '../../ajs-ng-communication.service';
import { AjsStateService } from '../../ajs-state.service';
import { states } from '../../core/application.states';
import { MediaRequester } from '../../requesters/media/media.requester';
import {
	IMediaActionModalData,
	MediaAction,
	MediaActionPopupComponent,
} from '../components/media-action-popup/media-action-popup.component';
import { MediaAdapter } from '../media.adapter';
import {
	bulkDeleteMedias,
	deleteMedia,
	deleteMediaFail,
	deleteMediaSuccess,
	duplicateMedia,
	duplicateMediaFail,
	duplicateMediaSuccess,
	fetchMediaListFail,
	fetchMediaListSuccess,
	initMediaList,
	navigateToMedia,
	navigateToMediaCreate,
	openDeleteConfirmationPopup,
	refreshMediaList,
	toggleMediaStatus,
} from './media.actions';

@Injectable()
export class MediaEffects {
	// ##########################################################
	// Navigation Effects
	// ##########################################################
	public navigateToMediaCreate = createEffect(
		() =>
			this.action$.pipe(
				ofType(navigateToMediaCreate),
				tap(() => this.stateService.go(states.MediaSelect))
			),
		{
			dispatch: false,
		}
	);

	public navigateToMedia = createEffect(
		() =>
			this.action$.pipe(
				ofType(navigateToMedia),
				tap((a) =>
					a.goToVisuTab
						? this.stateService.go(states.MediaVisualization, {
								id: `${a.id}`,
								type: `${a.mediaType}`,
								mode: 'visualisation',
						  })
						: this.stateService.go(states.MediaEdit, {
								id: `${a.id}`,
								type: `${a.mediaType}`,
						  })
				)
			),
		{
			dispatch: false,
		}
	);

	// ##########################################################
	// Business Effects
	// ##########################################################

	public initMediaList$ = createEffect(() =>
		this.action$.pipe(
			ofType(initMediaList),
			switchMap(() => this.mediaRequester.getMediaList()),
			map((response) =>
				fetchMediaListSuccess({
					mediaList: this.mediaAdapter.generateMediaList(response),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchMediaListFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public refreshMediaList$ = createEffect(() =>
		this.action$.pipe(
			ofType(refreshMediaList),
			switchMap(() => this.mediaRequester.getMediaList(false)),
			map((response) =>
				fetchMediaListSuccess({
					mediaList: this.mediaAdapter.generateMediaList(response),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchMediaListFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public toggleActivationStatus$ = createEffect(() =>
		this.action$.pipe(
			ofType(toggleMediaStatus),
			switchMap((action) =>
				this.mediaRequester.getMedia(action.mediaId, action.mediaType)
			),
			switchMap((media) =>
				this.mediaRequester.changeActivationStatusMedia({
					...media,
					metadata: {
						...media.metadata,
						actif: !media.metadata.actif,
					},
				})
			),
			map(() => refreshMediaList()),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(refreshMediaList());
				return caught;
			})
		)
	);

	public deleteMedia$ = createEffect(() =>
		this.action$.pipe(
			ofType(deleteMedia),
			switchMap((action) =>
				this.mediaRequester.deleteMedia(action.mediaId, action.mediaType)
			),
			map(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@media.delete.success:`
				);
				this.store.dispatch(deleteMediaSuccess());
				return refreshMediaList();
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(
					$localize`:i18n=@@media.delete.error:`
				);
				this.store.dispatch(
					deleteMediaFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public bulkDeleteMedias$ = createEffect(() =>
		this.action$.pipe(
			ofType(bulkDeleteMedias),
			switchMap((action) =>
				this.mediaRequester.deleteManyMedias(action.mediaIds)
			),
			map(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@media.delete.multiple.success:`
				);
				this.store.dispatch(deleteMediaSuccess());
				return refreshMediaList();
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(
					$localize`:i18n=@@media.delete.multiple.error:`
				);
				this.store.dispatch(
					deleteMediaFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public duplicateMedia$ = createEffect(() =>
		this.action$.pipe(
			ofType(duplicateMedia),
			switchMap((action) =>
				combineLatest([
					of(action),
					this.mediaRequester.duplicateMedia(action.mediaId, action.mediaType),
				])
			),
			map(([action, id]) => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@media.duplicate.success:`
				);
				return duplicateMediaSuccess({
					id,
					mediaType: action.mediaType,
				});
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(
					$localize`:i18n=@@media.duplicate.error:`
				);
				this.store.dispatch(duplicateMediaFail(error));
				return caught;
			})
		)
	);

	public navigateToMediaEditAfterDuplicate$ = createEffect(() =>
		this.action$.pipe(
			ofType(duplicateMediaSuccess),
			map((action) =>
				navigateToMedia({
					id: action.id,
					mediaType: action.mediaType,
					goToVisuTab: false,
				})
			)
		)
	);

	public deleteFailAction$ = createEffect(
		() =>
			this.action$.pipe(
				ofType(deleteMediaFail),
				tap(({ error }) => {
					const httpErrorsResponse = error as HttpErrorResponse;
					// eslint-disable-next-line camelcase
					const { delete_errors_report } = httpErrorsResponse.error;

					// eslint-disable-next-line camelcase
					if (
						delete_errors_report.total_deleted_element &&
						delete_errors_report.total_deleted_element !== 0
					) {
						this.store.dispatch(refreshMediaList());
					}
				})
			),
		{
			dispatch: false,
		}
	);

	// ##########################################################
	// Confirmation popup effects
	// ##########################################################
	public openDeleteConfirmationPopup$ = createEffect(() =>
		this.action$.pipe(
			ofType(openDeleteConfirmationPopup),
			switchMap((action) => {
				const modalRef = this.modalService.openPopup<IMediaActionModalData>(
					MediaActionPopupComponent,
					{
						title: $localize`:i18n=@@media.action.popup.delete.header:`,
						subTitle: '',
						mediaId: action.mediaId,
						element: action.mediaLabel,
						action: MediaAction.Delete,
					},
					{
						disableClose: true,
					}
				);
				return combineLatest([of(action), modalRef.afterClosed()]);
			}),
			filter(([, modalResult]) => modalResult !== undefined),
			map(([action]) => {
				if (action.mediaId.length === 1) {
					return deleteMedia({
						mediaId: action.mediaId[0],
						mediaType: action.mediaType[0],
					});
				} else {
					return bulkDeleteMedias({
						mediaIds: action.mediaId,
					});
				}
			})
		)
	);

	public constructor(
		private readonly action$: Actions,
		private readonly mediaRequester: MediaRequester,
		private readonly mediaAdapter: MediaAdapter,
		private readonly store: Store,
		private readonly stateService: AjsStateService,
		private readonly modalService: AppModalService,
		private readonly ngCommService: NgCommService
	) {}
}
